import { BridgeComponent } from "@hotwired/hotwire-native-bridge"

// One observer serves all cards, including long/infinite-scrolling lists.
const controllersByElement = new WeakMap()
let visibilityObserver

// Announce clean hero assets before the tap. Native deduplicates downloads and
// caches the header by destination. A click never waits for an image or reply.
export default class extends BridgeComponent {
  static component = "product-preview"
  static values = { title: String, brand: String, category: String, imageUrl: String }

  connect() {
    super.connect()
    visibilityObserver?.unobserve(this.element)
    controllersByElement.delete(this.element)
    // shouldLoad already gates supported apps. Let send() queue preloads while
    // the native adapter is still initializing on the first page.
    if (this.platformOptingOut) return
    visibilityObserver ||= new IntersectionObserver(entries => {
      for (const entry of entries) {
        controllersByElement.get(entry.target)?.visibilityChanged(entry.isIntersecting)
      }
    }, { rootMargin: "200px" })
    controllersByElement.set(this.element, this)
    visibilityObserver.observe(this.element)
  }

  disconnect() {
    visibilityObserver?.unobserve(this.element)
    controllersByElement.delete(this.element)
    this.visibilityChanged(false)
    super.disconnect()
  }

  visibilityChanged(nearViewport) {
    // Don't send cancellation messages for every initially offscreen card.
    if (nearViewport || this.nearViewport) this.announce(nearViewport ? "preload" : "cancel")
    this.nearViewport = nearViewport
  }

  visit(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey ||
        event.ctrlKey || event.shiftKey || event.altKey) return
    this.announce("prepare")
  }

  announce(event) {
    if (this.platformOptingOut || (event !== "cancel" && !this.element.isConnected) || this.element.hasAttribute("download") ||
        (this.element.target && this.element.target !== "_self")) return
    const url = new URL(this.element.href, window.location.href)
    if (url.origin !== window.location.origin) return

    this.send(event, {
      url: url.href,
      title: this.titleValue,
      brand: this.brandValue,
      category: this.categoryValue,
      imageURL: this.imageUrlValue ? new URL(this.imageUrlValue, window.location.href).href : null,
      origin: event === "prepare" ? this.imageOrigin() : null
    })
  }

  imageOrigin() {
    // UIKit uses a native image as the zoom source, so report the image's content
    // box without its card background, padding, badges or other HTML overlays.
    const image = this.element.querySelector("img")
    if (!image?.complete || !image.naturalWidth ||
        (window.visualViewport && window.visualViewport.scale !== 1)) return null

    const rect = image.getBoundingClientRect()
    const style = window.getComputedStyle(image)
    const inset = side => (parseFloat(style[`padding${side}`]) || 0) + (parseFloat(style[`border${side}Width`]) || 0)
    const origin = {
      x: rect.x + inset("Left"), y: rect.y + inset("Top"),
      width: rect.width - inset("Left") - inset("Right"),
      height: rect.height - inset("Top") - inset("Bottom"),
      viewportWidth: window.innerWidth
    }
    return Object.values(origin).every(Number.isFinite) && origin.width > 0 && origin.height > 0 ? origin : null
  }
}
